/**
 * "View Route" (3.2): the day on a map, in the product's own route view.
 *
 * This is not a demo drawing. `RouteIndexView` is the same component the command
 * centre opens from a provider row, mounted here in its `compact` form — the
 * header and the patient-location search box are command's, so the dialog
 * supplies its own title instead. Nothing on this path writes.
 *
 * The day it plots is read through the product's own `CommandIndexCombinedData`,
 * the same read **Optimize** makes, because the horizon read carries no address
 * coordinates. It costs one query per open and no drive-time call: the map draws
 * its line from the Google Directions service, not from the pricing path.
 *
 * Two things are deliberate. The read happens only once the dialog is mounted,
 * so opening the day menu costs nothing; and the whole tree is unmounted on
 * close, which is what stops the sixty-second `ProviderLocation` poll the route
 * view keeps while it is on screen.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import type { CommandIndexCombinedDataQuery } from "~/graphql/__generated__/graphql";
import type { LocalShift, LocalVisitResource } from "~/hooks/visitDragDropReducer";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { buildRouteLocalData, type RouteLocalData } from "~/views/command/routeLocalDataMapper";
import RouteIndexView from "~/views/route";
import { fetchCommandDay } from "../../data/mutations";
import { addDaysToKey, dateKeyAt, dateKeyStart, formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { useDemoState } from "../../store/useDemoStore";
import type { DateKey } from "@caredna-marlin/utils/homeHealthDemo/state";

/** The route mapper takes command's local shapes, which are this query's rows without `__typename`. */
function nonEmptyVisit(visitResource: CommandIndexCombinedDataQuery["visitResource"][number]): visitResource is LocalVisitResource {
  return Boolean(visitResource.visit);
}

/**
 * Everything the mounted map depends on, in one object. `RouteIndexView` memoizes
 * its markers on the identity of these props and the map asks Google for driving
 * directions whenever they change, so they are computed once per open and never
 * rebuilt inline in the render.
 */
interface DayRoute {
  route: RouteLocalData;
  date: Date;
  rangeStart: Date;
  rangeEnd: Date;
}

export function RouteDayDialog({ date, providerId, providerName, onClose }: { date: DateKey; providerId: number; providerName: string; onClose: () => void }) {
  const client = useApolloClient();
  const orgConfig = useOrgConfig();
  const tz = useDemoState().config.branch.timeZone;
  // The route view renders a skeleton for ever when the key is empty, which on
  // stage reads as a hung dialog. Say so instead, and read nothing.
  const hasMapsKey = Boolean(orgConfig.GOOGLE_MAPS_WEB_API_KEY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [day, setDay] = useState<DayRoute>();

  const load = useCallback(async () => {
    setError(undefined);
    try {
      const rangeStart = dateKeyStart(date, tz);
      const rangeEnd = dateKeyStart(addDaysToKey(date, 1), tz);
      // Noon in the branch zone, because the mapper and the route view both bucket
      // the day with the browser's own `startOfDay`; midnight would fall on the
      // wrong date for a presenter whose machine is not on branch time.
      const noon = dateKeyAt(date, "12:00", tz);
      const data = await fetchCommandDay(client, { filterStartTime: rangeStart.toISOString(), filterEndTime: rangeEnd.toISOString() });
      const shifts: LocalShift[] = data.shift;
      const shift = shifts.find((s) => s.providerId === providerId && s.status !== "canceled");
      setDay({
        date: noon,
        rangeStart,
        rangeEnd,
        route: buildRouteLocalData({
          providerId,
          date: noon,
          localVisitResources: data.visitResource.filter(nonEmptyVisit),
          localCalendarBlocks: data.calendarBlock,
          localShifts: shifts,
          // The shift row carries the clinician's home coordinates, so the Start
          // marker needs no second read. Without a shift there is simply no Start.
          providerAddressCoordinates: shift?.provider?.addressCoordinates ?? null,
          orgConfig,
        }),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client, date, providerId, tz, orgConfig]);

  // Once per open, and only once. `useOrgConfig()` hands back a fresh object when
  // its own query resolves, so a plain dependency on it would read the day a
  // second time; the dialog is mounted for one day and reads it one time.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || orgConfig.loading) return;
    started.current = true;
    if (!hasMapsKey) {
      setLoading(false);
      return;
    }
    void load();
  }, [orgConfig.loading, hasMapsKey, load]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{formatInBranchZone(new Date(`${date}T12:00:00Z`), "UTC", "EEEE, MMMM d")} Route</DialogTitle>
      <DialogContent>
        {!loading && !hasMapsKey && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            The map is not configured for this organization, so the day cannot be drawn here. The day&apos;s stops are on the week behind this dialog.
          </Typography>
        )}
        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Reading the day&apos;s stops…</Typography>
          </Stack>
        )}
        {error && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {hasMapsKey && day && (
          <RouteIndexView
            providerId={providerId}
            providerName={providerName}
            date={day.date}
            compact
            rangeStart={day.rangeStart}
            rangeEnd={day.rangeEnd}
            localVisitResources={day.route.localVisitResources}
            localCalendarBlocks={day.route.localCalendarBlocks}
            localProviderContext={day.route.providerContext}
            localShiftContext={day.route.shiftContext}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
